"use client";

import { useEffect, useState } from "react";
import { ChevronUp, X } from "lucide-react";

import { cn } from "@/lib/utils/utils";
import { pad2 } from "@/lib/blog/format";

import { useBlogReader } from "../BlogReaderProvider";

/**
 * The index on sm and md, where there is no room for a column.
 *
 * A slim sticky bar carries the same three facts the desktop rail does — which
 * section, how many, how far — and opens a sheet with the full list. The bar is
 * 48px because anything taller starts competing with the text for the top of
 * the screen on a phone, which is the whole reason the column was dropped.
 */
const BlogMobileIndex = () => {
  const { toc, sectionLabel, active, progress, started } = useBlogReader();
  const [open, setOpen] = useState(false);

  // A sheet that survives a rotation or a resize into desktop would leave an
  // overlay with no way to close it once the button it belongs to is hidden.
  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(false);
    window.addEventListener("resize", close);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (toc.length === 0) return null;

  const current = toc.find((item) => item.index === active) ?? toc[0];

  return (
    <>
      <div
        aria-hidden={!started}
        className={cn(
          "sticky top-0 z-30 border-b border-rule bg-background/92 backdrop-blur-[10px] transition-[opacity,transform] duration-500 ease-[cubic-bezier(.22,.61,.36,1)] lg:hidden",
          started
            ? "translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0",
        )}
      >
        <div className="h-[3px] w-full bg-rule">
          <div
            className="h-[3px] w-full origin-left bg-ink transition-transform duration-150 ease-linear"
            style={{ transform: `scaleX(${progress})` }}
          />
        </div>

        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded={open}
          className="flex min-h-12 w-full items-center gap-3 px-4 text-left"
        >
          <span className="text-[13px] font-semibold text-ink tabular-nums">
            {pad2(active)}
            <span className="font-normal text-ink-mute">
              {" "}
              / {pad2(toc.length)}
            </span>
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-ink-soft">
            {current.title}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] text-ink-mute uppercase">
            Contents
            <ChevronUp size={14} aria-hidden="true" className="rotate-180" />
          </span>
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close contents"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-ink/45"
          />

          <div className="absolute inset-x-0 bottom-0 max-h-[72vh] overflow-y-auto rounded-t-2xl border-t border-rule bg-background pb-8">
            <div className="sticky top-0 flex items-center justify-between gap-4 border-b border-rule bg-background px-5 py-4">
              <p className="text-xs tracking-[0.14em] text-ink-mute uppercase">
                {sectionLabel} list
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close contents"
                className="inline-flex size-11 items-center justify-center rounded-lg text-ink"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <ol className="px-2 py-2">
              {toc.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={() => setOpen(false)}
                    aria-current={item.index === active ? "true" : undefined}
                    className={cn(
                      "flex min-h-12 items-center gap-4 rounded-xl px-3 py-3 text-[15px] leading-[1.4]",
                      item.index === active
                        ? "bg-paper font-medium text-ink"
                        : "text-ink-soft",
                    )}
                  >
                    <span
                      className={cn(
                        "text-[13px] tabular-nums",
                        item.index === active ? "text-ink" : "text-ink-mute",
                      )}
                    >
                      {pad2(item.index)}
                    </span>
                    <span className="flex-1">{item.title}</span>
                    {item.index === active && (
                      <span
                        aria-hidden="true"
                        className="size-2 rounded-full bg-primary"
                      />
                    )}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogMobileIndex;
